import { useState } from "react";

import { useAutoAnimate } from "@formkit/auto-animate/react";
import { differenceBy, isEmpty } from "lodash/fp";

import { Collapse, Typography } from "@mui/material";

import SeeAllButton from "@app/analytics/Recommendations/components/SeeAllButton";
import type { GroupedRecommendation } from "@app/analytics/types";
import type { Pagination } from "@app/common/hooks/usePagination";
import { ELLIPSIS_SX } from "@app/common/styles";

import Stack, { Row } from "@rui/Stack";
import { StickyContainer } from "@rui/styles";

import Recommendation, {
  RecommendationProps,
} from "./components/Recommendation";
import RecommendationActionsDropdownMenu from "./components/RecommendationActionsDropdownMenu";
import RecommendationCountChip from "./components/RecommendationCountChip";
import RecommendationsSampleDataBanner from "./components/RecommendationsSampleDataBanner";
import {
  PaginatorButton,
  RecommendationDetailContent,
  RecommendationDetailHeading,
} from "./styles";

interface RecommendationDetailProps {
  actionLoading: boolean;
  /** Grouped recommendation with the recommendations filtered down to
   * only those with actions that match the applied filters */
  filteredRecommendation?: GroupedRecommendation;
  isSample: boolean;
  onAcceptAll: (rec: GroupedRecommendation) => void;
  onAcceptRecommendation: RecommendationProps["onAccept"];
  onDisableSampleData: () => void;
  onRejectAll: (rec: GroupedRecommendation) => void;
  onRejectRecommendation: RecommendationProps["onReject"];
  pagination: Pagination;
  /** Grouped recommendation with all recommendations */
  unfilteredRecommendation?: GroupedRecommendation;
}

const RecommendationDetail = ({
  actionLoading,
  filteredRecommendation,
  isSample,
  onAcceptAll,
  onAcceptRecommendation,
  onDisableSampleData,
  onRejectAll,
  onRejectRecommendation,
  pagination,
  unfilteredRecommendation,
}: RecommendationDetailProps) => {
  const [showUnfilteredRecommendations, setShowUnfilteredRecommendations] =
    useState(false);

  const [contentRef] = useAutoAnimate();

  // this panel will be collapsed when there's no recommendation, so we can safely show nothing.
  // there should never be a case where only one of these is defined, but check both just to
  // reduce the number of safe access checks we have to do
  if (!filteredRecommendation || !unfilteredRecommendation) return null;

  const filteredRecommendations = filteredRecommendation.recommendations;
  const otherRecommendations = differenceBy(
    "id",
    unfilteredRecommendation.recommendations,
    filteredRecommendation.recommendations
  );

  const recommendation = filteredRecommendation;

  const recommendationProps = {
    actionLoading,
    isSample,
    onAccept: onAcceptRecommendation,
    onReject: onRejectRecommendation,
  };

  const handleAcceptAll = () => {
    onAcceptAll(recommendation);
  };
  const handleRejectAll = () => {
    onRejectAll(recommendation);
  };

  return (
    <Stack>
      <StickyContainer>
        <RecommendationDetailHeading variant="apart" spacing={2}>
          <PaginatorButton
            direction="back"
            onClick={pagination.goToPreviousPage}
            disabled={!pagination.hasPrevious}
          />
          <Row spacing={1} variant="center" sx={{ overflow: "hidden" }}>
            <Typography
              variant="h3"
              sx={ELLIPSIS_SX}
              title={recommendation.meetingName}
            >
              {recommendation.meetingName}
            </Typography>
            <RecommendationCountChip>
              {recommendation.recommendations.length}
            </RecommendationCountChip>
            <RecommendationActionsDropdownMenu
              onAcceptAll={handleAcceptAll}
              onRejectAll={handleRejectAll}
            />
          </Row>
          <PaginatorButton
            direction="forward"
            onClick={pagination.goToNextPage}
            disabled={!pagination.hasNext}
          />
        </RecommendationDetailHeading>
        <RecommendationsSampleDataBanner
          autoDismiss={!isSample}
          onDisableSampleData={onDisableSampleData}
        />
      </StickyContainer>
      <RecommendationDetailContent spacing={2} ref={contentRef}>
        {filteredRecommendations.map((rec) => (
          <Recommendation
            key={rec.id}
            {...recommendationProps}
            recommendation={rec}
          />
        ))}
        {!isEmpty(otherRecommendations) && (
          <>
            <Collapse in={showUnfilteredRecommendations} unmountOnExit>
              <Stack spacing={2}>
                {otherRecommendations.map((rec) => (
                  <Recommendation
                    key={rec.id}
                    {...recommendationProps}
                    recommendation={rec}
                  />
                ))}
              </Stack>
            </Collapse>
            <SeeAllButton
              expandLabel="See all recommendations for this meeting"
              expanded={showUnfilteredRecommendations}
              onCollapse={() => setShowUnfilteredRecommendations(false)}
              onExpand={() => setShowUnfilteredRecommendations(true)}
            />
          </>
        )}
      </RecommendationDetailContent>
    </Stack>
  );
};

export default RecommendationDetail;
